import { useStore } from '@nanostores/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '~/components/ui/Button';
import { IconButton } from '~/components/ui/IconButton';
import { StatusDot } from '~/components/ui/StatusDot';
import { TextShimmer } from '~/components/ui/TextShimmer';
import * as Tooltip from '~/components/ui/Tooltip';
import { chatHooks } from '~/lib/chat-hooks';
import { COPIED_MESSAGE_TIMEOUT } from '~/lib/constants';
import { getLastNonImportMessage, isDeploymentMessage, isFailedDeploymentMessage } from '~/lib/deploy';
import { importStore } from '~/lib/projects';
import { descriptionStore } from '~/lib/stores/description';
import { dropdownAnimationVariants } from '~/utils/animationVariants';
import { classNames } from '~/utils/classNames';
import { buildUrl, removeProtocol } from '~/utils/url';

const TriggerButton = ({ disabled, 'aria-controls': ariaControls }: { disabled: boolean; 'aria-controls': string }) => {
  return (
    <DropdownMenu.Trigger asChild>
      <Button
        className="flex gap-1.7 shrink-0 h-8 text-sm px-3"
        variant="high-contrast"
        size="sm"
        truncate={false}
        disabled={disabled}
        aria-controls={ariaControls}
      >
        Publish
      </Button>
    </DropdownMenu.Trigger>
  );
};

const Popup = ({ children, open, id }: { children: React.ReactNode; open: boolean; id: string }) => {
  return (
    <AnimatePresence>
      {open && (
        <DropdownMenu.Portal forceMount>
          <DropdownMenu.Content
            id={id}
            className="z-visibility-dropdown"
            asChild
            forceMount
            avoidCollisions={false}
            sideOffset={3}
            align="end"
          >
            <motion.div initial="close" animate="open" exit="close" variants={dropdownAnimationVariants}>
              <div className="bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 p-2 border border-bolt-elements-borderColor rounded-md text-sm select-none flex flex-col gap-3 p-4 w-78">
                {children}
                <DropdownMenu.Arrow className="fill-bolt-elements-borderColor" />
              </div>
            </motion.div>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      )}
    </AnimatePresence>
  );
};

const SocialShare = () => {
  const projectName = useStore(descriptionStore);
  const { deploymentInfo } = useStore(importStore);

  if (!deploymentInfo) {
    return null;
  }

  const providers = [
    {
      name: 'X',
      icon: 'i-ph:x-logo',
      href: buildUrl('https://x.com/intent/tweet', {
        text: `I just published my new app with Bolt: ${projectName}`,
        url: deploymentInfo.site_url,
      }),
    },
    {
      name: 'LinkedIn',
      icon: 'i-mdi:linkedin',
      href: buildUrl('https://www.linkedin.com/feed/', {
        shareActive: 'true',
        text: `I just published my new app with Bolt: ${projectName}\n${deploymentInfo.site_url}`,
      }),
    },
    {
      name: 'Reddit',
      icon: 'i-mdi:reddit',
      href: buildUrl('https://www.reddit.com/r/boltnewbuilders/submit', {
        title: `My new app built with Bolt: ${projectName}`,
        url: deploymentInfo.site_url,
      }),
    },
  ];

  return (
    <Tooltip.Provider>
      <div>
        <p className="text-xs text-bolt-ds-textTertiary">Share it with friends and coworkers</p>
        <div className="flex items-center gap-2 mt-2">
          {providers.map(({ name, icon, href }) => (
            <Tooltip.Root key={icon} delayDuration={300}>
              <Tooltip.Trigger asChild>
                <a href={href} target="_blank" className="hover:scale-110 transition">
                  <span className={classNames('size-5 block', icon)} />
                </a>
              </Tooltip.Trigger>
              <Tooltip.Content>Share on {name}</Tooltip.Content>
            </Tooltip.Root>
          ))}
        </div>
      </div>
    </Tooltip.Provider>
  );
};

const DeploymentInfo = ({
  isDeploying,
  onManageCustomDomain,
}: {
  isDeploying: boolean;
  onManageCustomDomain: () => void;
}) => {
  const { deploymentInfo } = useStore(importStore);

  const [wasCopied, setWasCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (!deploymentInfo?.site_url) {
      return;
    }

    navigator.clipboard.writeText(deploymentInfo.site_url);

    setWasCopied(true);

    setTimeout(() => {
      setWasCopied(false);
    }, COPIED_MESSAGE_TIMEOUT);
  }, [deploymentInfo]);

  return (
    <div className="grid grid-cols-[min-content_max-content] auto-rows-auto self-start gap-2 gap-y-1">
      <span className="block i-ph:globe-simple size-4 shrink-0 text-bolt-ds-iconSecondary" />

      {!deploymentInfo && !isDeploying && (
        <motion.span className="text-xs font-medium">App is not published yet</motion.span>
      )}

      {isDeploying && (
        <TextShimmer
          className="text-xs font-medium leading-4 [--base-color:var(--bolt-elements-textTertiary)] dark:[--base-color:var(--bolt-elements-textSecondary)] [--base-gradient-color:var(--bolt-elements-textPrimary)]"
          duration={1.5}
          spread={2}
          length={120}
        >
          {deploymentInfo ? 'Updating...' : 'Publishing...'}
        </TextShimmer>
      )}

      {deploymentInfo && !isDeploying && (
        <div className="flex items-center gap-2">
          <motion.a
            href={deploymentInfo.site_url}
            target="_blank"
            className="text-xs font-medium truncate hover:underline inline-block mt-0.25"
          >
            {wasCopied ? 'Copied!' : removeProtocol(deploymentInfo.site_url)}
          </motion.a>

          {!wasCopied && (
            <IconButton
              className="bg-transparent -my-1"
              title="Copy link"
              variant="base"
              size="2xs"
              onClick={handleCopy}
              icon={wasCopied ? 'i-ph:check' : 'i-ph:copy'}
            />
          )}
        </div>
      )}

      {deploymentInfo && deploymentInfo.is_custom_domain && (
        <Button
          className="col-start-2 col-span-2 row-start-2 w-max"
          size="sm"
          variant="link-color"
          onClick={onManageCustomDomain}
        >
          Manage custom domain
        </Button>
      )}
    </div>
  );
};

const DeploymentTime = () => {
  const { deploymentInfo } = useStore(importStore);

  if (!deploymentInfo || !deploymentInfo.updated_at) {
    return null;
  }

  return (
    <AnimatePresence>
      {deploymentInfo && (
        <motion.div
          className="flex items-center gap-2"
          transition={{ duration: 0.1 }}
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: 'auto', marginTop: '0.5rem' }}
        >
          <span className="block i-ph:clock size-4 text-bolt-ds-iconSecondary" />
          <span className="text-xs mt-0.5">
            {deploymentInfo.updated_at &&
              formatDistanceToNow(deploymentInfo.updated_at, {
                addSuffix: true,
              })}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const DeploymentStatus = ({ isDeploying }: { isDeploying: boolean }) => {
  const chatMessages = useStore(chatHooks.messages);

  const deploymentStatus = useMemo(() => {
    const lastNonImportMessage = getLastNonImportMessage(chatMessages);

    if (!lastNonImportMessage || !isDeploymentMessage(lastNonImportMessage)) {
      return 'outdated';
    }

    const deploymentMessage = lastNonImportMessage;

    if (isFailedDeploymentMessage(deploymentMessage)) {
      return 'failed';
    }

    if (deploymentMessage.status === 'streaming') {
      return 'pending';
    }

    if (deploymentMessage.status === 'complete') {
      return 'deployed';
    }

    return 'outdated';
  }, [chatMessages]);

  return (
    <AnimatePresence>
      {!isDeploying && (
        <motion.div
          className="flex items-center gap-2"
          transition={{ duration: 0.1 }}
          initial={{ opacity: 0, height: 0, marginTop: 0 }}
          exit={{ opacity: 0, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, height: 'auto', marginTop: '0.5rem' }}
        >
          <StatusDot
            status={
              {
                deployed: 'success' as const,
                failed: 'error' as const,
                outdated: 'pending' as const,
                pending: 'pending' as const,
              }[deploymentStatus]
            }
          />
          <span className="text-xs font-medium">
            {deploymentStatus === 'deployed'
              ? 'Up to date'
              : deploymentStatus === 'outdated'
                ? 'Unpublished changes'
                : 'Failed to publish'}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Deploy = {
  TriggerButton,
  Popup,
  SocialShare,
  DeploymentInfo,
  DeploymentTime,
  DeploymentStatus,
};
